"use client";

import { useEffect, useRef, useState } from "react";
import { messageFor } from "./errors";
import { safePath } from "@/lib/domain/paths";
import { fmt, t, type Lang } from "@/lib/i18n/index";

/** Where to land afterwards: one path on this site, or the front page. The
 *  rule is `lib/domain/paths.ts`, shared with the proxy that writes this
 *  parameter and with the Google handshake that carries it — one of them
 *  being laxer than the others is the whole of how a sign-in page becomes a
 *  redirect to somebody else's site. */
export function safeNext(next: string | null | undefined): string {
  return safePath(next) ?? "/";
}

async function post(path: string, body: unknown): Promise<{ status: number; data: Record<string, unknown> }> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { status: res.status, data };
}

function Field(props: {
  id: string; label: string; type: string; autoComplete: string;
  value: string; onChange: (v: string) => void; inputMode?: "numeric"; pattern?: string; autoFocus?: boolean;
}) {
  const { label, onChange, ...rest } = props;
  return (
    <div>
      <label htmlFor={props.id}>{label}</label>
      <input {...rest} name={props.id} required spellCheck={false} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/** The line under the form: what went wrong, or — on a machine with no mail
 *  account — where the code actually went. */
function Note({ error, hint }: { error: string | null; hint: string | null }) {
  if (error) return <p className="auth-note bad" role="alert">{error}</p>;
  if (hint) return <p className="auth-note">{hint}</p>;
  return null;
}

/** `said` is what the Google callback sent them back with, as an i18n key in
 *  the address bar: a navigation cannot be answered with JSON, so the refusal
 *  arrives here (lib/platform/http.ts `openGet`). */
export function SignIn({ lang, next, said }: { lang: Lang; next?: string | null; said?: string | null }) {
  const s = t(lang);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(said ? messageFor(lang, said) : null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { status, data } = await post("/api/auth/login", { email, password });
    if (status === 200) {
      // A full load, not a router push: everything behind this page is
      // rendered for whoever the cookie now says we are.
      window.location.href = safeNext(next);
      return;
    }
    setBusy(false);
    setError(messageFor(lang, data.error as string));
  }

  return (
    <form className="auth-form" method="post" onSubmit={submit} noValidate>
      <Field id="email" label={s["auth.email"]} type="email" autoComplete="username" value={email} onChange={setEmail} autoFocus />
      <Field id="password" label={s["auth.password"]} type="password" autoComplete="current-password" value={password} onChange={setPassword} />
      <div className="auth-row">
        <a className="more" href="/forgot" data-track="auth.forgot">{s["auth.forgot"]}</a>
      </div>
      <Note error={error} hint={null} />
      <button className="btn primary wide" type="submit" disabled={busy}>
        {busy ? s["auth.working"] : s["auth.signIn"]}
      </button>
    </form>
  );
}

/**
 * Signing up is two screens on one page: the address and a password, then
 * the six digits that went to that address. The account does not exist
 * between them (0010) — which is why this holds the address in state and the
 * server holds everything else.
 */
/**
 * `cooldown` is handed in rather than imported: the second between letters
 * is the server's rule (platform/accounts.ts), and importing that module
 * from a client component would pull `pg` into the browser bundle — the trap
 * block 4c walked into. The page is a server component and may read it, so
 * it does, and passes the number.
 */
export function SignUp({ lang, cooldown, minutes }: { lang: Lang; cooldown: number; minutes: number }) {
  const s = t(lang);
  const [step, setStep] = useState<"form" | "code">("form");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  const codeBox = useRef<HTMLInputElement>(null);

  // The cooldown the server keeps, counted down where they can see it.
  useEffect(() => {
    if (wait <= 0) return;
    const id = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  useEffect(() => {
    if (step === "code") codeBox.current?.focus();
  }, [step]);

  function took(data: Record<string, unknown>) {
    // "The letter did not go out, the code is on the server's console" is
    // the truth on a machine with no mail account, and it is better said
    // than hidden behind a screen asking for a code nobody received.
    setHint(data.note === "console" ? s["auth.code.devNote"] : null);
    // The letter did not go out at all. Better said than left as a screen
    // asking for a code that is never going to arrive.
    setError(data.note === "failed" ? s["auth.err.mail"] : null);
    setWait(cooldown);
  }

  async function start(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    const { status, data } = await post("/api/auth/register", { email, password, lang, tz });
    setBusy(false);
    if (status !== 200) return setError(messageFor(lang, data.error as string));
    took(data);
    setStep("code");
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { status, data } = await post("/api/auth/verify", { email, code });
    if (status === 200) {
      // The verified account now owns a resumable onboarding record.
      window.location.href = "/onboarding";
      return;
    }
    setBusy(false);
    setCode("");
    setError(messageFor(lang, data.error as string));
  }

  async function again() {
    setBusy(true);
    setError(null);
    const { status, data } = await post("/api/auth/resend", { email, lang });
    setBusy(false);
    if (status !== 200) {
      // Nothing outstanding to resend — the ten minutes ran out, or the
      // guesses did. The way on from there is the form, not a screen whose
      // only button has just been refused.
      if (data.error === "auth.err.codeGone") {
        setStep("form");
        setCode("");
      }
      return setError(messageFor(lang, data.error as string));
    }
    took(data);
  }

  if (step === "form") {
    return (
      <form className="auth-form" method="post" onSubmit={start} noValidate>
        <Field id="email" label={s["auth.email"]} type="email" autoComplete="username" value={email} onChange={setEmail} autoFocus />
        <Field id="password" label={s["auth.password"]} type="password" autoComplete="new-password" value={password} onChange={setPassword} />
        <Note error={error} hint={null} />
        <button className="btn primary wide" type="submit" disabled={busy}>
          {busy ? s["auth.working"] : s["auth.signUp"]}
        </button>
      </form>
    );
  }

  return (
    <form className="auth-form" method="post" onSubmit={verify} noValidate>
      <p className="auth-said">{fmt(s["auth.code.sent"], email, minutes)}</p>
      <div>
        <label htmlFor="code">{s["auth.code.label"]}</label>
        <input
          id="code" name="code" ref={codeBox} value={code} required
          inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="one-time-code"
          className="code-box" onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        />
      </div>
      <Note error={error} hint={hint} />
      <button className="btn primary wide" type="submit" disabled={busy || code.length !== 6}>
        {busy ? s["auth.working"] : s["auth.code.submit"]}
      </button>
      <div className="auth-row spread">
        <button type="button" className="btn quiet sm" onClick={again} disabled={busy || wait > 0}>
          {wait > 0 ? fmt(s["auth.code.resendIn"], wait) : s["auth.code.resend"]}
        </button>
        <button type="button" className="btn quiet sm" data-track="auth.change_email" onClick={() => { setStep("form"); setError(null); setCode(""); }}>
          {s["auth.code.another"]}
        </button>
      </div>
    </form>
  );
}

/**
 * Forgetting the password, which is the same two screens as signing up: an
 * address, then the six digits that went to it — with the new password on
 * the second screen rather than the first.
 *
 * On the second screen rather than the first on purpose. A password typed
 * before the code is a password held on the server for somebody who has not
 * yet shown they read that mailbox; here nothing is written until the code
 * comes back, and what comes back with it is the whole of the change.
 */
export function Forgot({ lang, cooldown, minutes }: { lang: Lang; cooldown: number; minutes: number }) {
  const s = t(lang);
  const [step, setStep] = useState<"form" | "code" | "done">("form");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return;
    const id = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  async function ask(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { status, data } = await post("/api/auth/forgot", { email, lang });
    setBusy(false);
    if (status !== 200) return setError(messageFor(lang, data.error as string));
    setHint(data.note === "console" ? s["auth.code.devNote"] : s["auth.reset.sent"]);
    setError(data.note === "failed" ? s["auth.err.mail"] : null);
    setWait(cooldown);
    setStep("code");
  }

  async function again() {
    setBusy(true);
    setError(null);
    const { status, data } = await post("/api/auth/forgot", { email, lang });
    setBusy(false);
    if (status !== 200) return setError(messageFor(lang, data.error as string));
    setHint(data.note === "console" ? s["auth.code.devNote"] : s["auth.reset.sent"]);
    setWait(cooldown);
  }

  async function set(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { status, data } = await post("/api/auth/reset", { email, code, password });
    setBusy(false);
    if (status === 200) {
      // Said, not skipped past. This is the one screen in the portal whose
      // side effect reaches other devices — every session ended, the
      // extension's key turned off — and somebody who finds themselves
      // signed out on their phone an hour later should have been told why.
      setStep("done");
      return;
    }
    setCode("");
    if (data.error === "auth.err.codeGone") setStep("form");
    setError(messageFor(lang, data.error as string));
  }

  if (step === "done") {
    return (
      <div className="auth-form">
        <p className="auth-said">{s["auth.reset.done"]}</p>
        <a className="btn primary wide" href="/">{s["billing.back"]}</a>
      </div>
    );
  }

  if (step === "form") {
    return (
      <form className="auth-form" method="post" onSubmit={ask} noValidate>
        <p className="auth-said">{s["auth.reset.lede"]}</p>
        <Field id="email" label={s["auth.email"]} type="email" autoComplete="username" value={email} onChange={setEmail} autoFocus />
        <Note error={error} hint={null} />
        <button className="btn primary wide" type="submit" disabled={busy}>
          {busy ? s["auth.working"] : s["auth.code.submit"]}
        </button>
      </form>
    );
  }

  return (
    <form className="auth-form" method="post" onSubmit={set} noValidate>
      <p className="auth-said">{fmt(s["auth.code.sent"], email, minutes)}</p>
      <div>
        <label htmlFor="code">{s["auth.code.label"]}</label>
        <input
          id="code" name="code" value={code} required autoFocus
          inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="one-time-code"
          className="code-box" onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        />
      </div>
      <Field id="password" label={s["auth.reset.new"]} type="password" autoComplete="new-password" value={password} onChange={setPassword} />
      <Note error={error} hint={hint} />
      <button className="btn primary wide" type="submit" disabled={busy || code.length !== 6}>
        {busy ? s["auth.working"] : s["auth.reset.submit"]}
      </button>
      <div className="auth-row spread">
        {/* The countdown used to be a line of text with nothing to press at
            the end of it — it said "you can ask again in 60s" and then went
            blank. Same shape as the sign-up screen now: the button is the
            countdown. */}
        <button type="button" className="btn quiet sm" onClick={again} disabled={busy || wait > 0}>
          {wait > 0 ? fmt(s["auth.code.resendIn"], wait) : s["auth.code.resend"]}
        </button>
        <button type="button" className="btn quiet sm" data-track="auth.change_email" onClick={() => { setStep("form"); setError(null); setCode(""); }}>
          {s["auth.code.another"]}
        </button>
      </div>
    </form>
  );
}
